/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { chain, compact, isEmpty, map, noop, result } from 'underscore';
import { FontIcon, IconButton, KeyCodes } from '@fluentui/react';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { AudienceStatus } from './audience-status';
import { formatAgeRanges } from '../constants/age-range';
import { formatGenders } from '../constants/gender';
import { modelToViewModelForAudienceGroup } from '../model/audience-group';
import { getAudienceCardStyle } from '../styles/audience-card';

const UnstyledAudienceCard = (props) => {
  const {
    audienceGroup,
    classes,
    i18n,
    isReadOnlyUser,
    onSelectAudienceSignal,
    onEditAudienceSignal,
    onRemoveAudienceSignal,
    onRenderActions,
    showActions,
    enableFocus,
  } = props;

  const serverContext = useContext(ServerContext);
  const {
    DynamicPermissions,
    Permissions,
  } = serverContext;

  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const isLinkedInTargetingEnabled = permissions && permissions.dynamic && permissions.dynamic.AudienceSignalTargetSupportForPmax;

  const {
    data: {
      Id,
      Name,
      CustomerData,
      Interests,
      AgeRanges,
      GenderTypes,
      Profiles,
    },
  } = useMemo(() => modelToViewModelForAudienceGroup(audienceGroup), [audienceGroup]);

  const ageRanges = useMemo(() => formatAgeRanges(AgeRanges, i18n), [AgeRanges, i18n]);

  const genders = useMemo(() => formatGenders(GenderTypes, i18n), [GenderTypes, i18n]);

  const demographics = useMemo(() => compact([ageRanges, genders]).join(i18n.getString(_TL_('; '))), [ageRanges, genders, i18n]);

  const customerData = useMemo(
    () => chain(CustomerData)
      .map(audience => `{{dataTag}}${result(audience, 'Name')}{{/dataTag}}`)
      .join(i18n.getString(_TL_(', ')))
      .value(),
    [CustomerData, i18n]
  );

  const interests = useMemo(
    () => map(Interests, audience => result(audience, 'Name', '').replace(/^\//, '')),
    [Interests]
  );

  const profiles = useMemo(() => {
    if (isLinkedInTargetingEnabled) {
      return compact(map(Profiles, profile => result(profile, 'Name', '')));
    }

    return [];
  }, [Profiles, isLinkedInTargetingEnabled]);

  const renderActions = useCallback(() => (onRenderActions ? onRenderActions() : (
    <>
      <IconButton
        className="audience-edit"
        iconProps={{ iconName: 'Edit' }}
        title={i18n.getString(_TL_('Edit'))}
        onClick={() => onEditAudienceSignal(Id)}
      />
      <IconButton
        className="audience-remove"
        iconProps={{ iconName: 'Clear' }}
        title={i18n.getString(_TL_('Remove'))}
        onClick={() => onRemoveAudienceSignal(Id)}
      />
    </>
  )), [Id, i18n, onEditAudienceSignal, onRemoveAudienceSignal, onRenderActions]);

  const onSelect = useCallback(() => {
    onSelectAudienceSignal(audienceGroup);
  }, [audienceGroup, onSelectAudienceSignal]);

  const onSelectByKey = useCallback((event) => {
    if (event.keyCode === KeyCodes.enter && event.currentTarget === event.target) {
      onSelect(event);
    }
  }, [onSelect]);

  return (
    <div
      className={classNames(['audience-card', classes.cardContainer])}
      {...enableFocus ? {
        tabIndex: 0,
        onClick: onSelect,
        onKeyDown: onSelectByKey,
      } : {}}
    >
      <div className="audience-header">
        <div className={classNames(['audience-name', showActions ? 'audience-highlight' : ''])}>{Name}</div>
        {!isReadOnlyUser && showActions && renderActions()}
      </div>
      <div className="audience-body">
        {!isEmpty(demographics) && (
          <div className="audience-demographics">
            <FontIcon className="audience-icon" aria-label={i18n.getString(_TL_('Demographics'))} iconName="People" />
            <ReactTemplate
              model={{
                dataTag: text => <span>{text}</span>,
              }}
              template={demographics}
            />
          </div>
        )}
        {!isEmpty(customerData) && (
          <div className="audience-customer-data">
            <FontIcon className="audience-icon" aria-label={i18n.getString(_TL_('Your Data'))} iconName="List" />
            <ReactTemplate
              model={{
                dataTag: text => <span>{text}</span>,
              }}
              template={customerData}
            />
          </div>
        )}
        {!isEmpty(interests) && (
          <div className="audience-interests">
            {map(interests, interest => (
              <span className="audience-interest" key={interest}>{interest}</span>
            ))}
          </div>
        )}
        {!isEmpty(profiles) && (
          <div className="audience-profiles">
            {map(profiles, profile => (
              <span className="audience-profile" key={profile}>{profile}</span>
            ))}
          </div>
        )}
        <AudienceStatus
          audienceGroup={audienceGroup}
          i18n={i18n}
        />
      </div>
    </div>
  );
};

UnstyledAudienceCard.propTypes = {
  audienceGroup: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
    Dimensions: PropTypes.arrayOf(PropTypes.shape({
      Type: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    cardContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isReadOnlyUser: PropTypes.bool,
  onSelectAudienceSignal: PropTypes.func,
  onEditAudienceSignal: PropTypes.func,
  onRemoveAudienceSignal: PropTypes.func,
  onRenderActions: PropTypes.func,
  showActions: PropTypes.bool,
  enableFocus: PropTypes.bool,
};

UnstyledAudienceCard.defaultProps = {
  isReadOnlyUser: true,
  onSelectAudienceSignal: noop,
  onEditAudienceSignal: noop,
  onRemoveAudienceSignal: noop,
  onRenderActions: null,
  showActions: false,
  enableFocus: false,
};

export const AudienceCard = withDefaultStyles(UnstyledAudienceCard, getAudienceCardStyle);
